import * as THREE from 'three';

/**
 * Generates a tileable-ish procedural PBR texture set (albedo, normal,
 * roughness, AO) for a surface, using canvas-based value noise. There are
 * no photographic texture assets in this project (no access to an asset
 * CDN) — this is the same placeholder-first approach as Milestone 1's
 * hand-authored tree GLTF, applied to textures instead of geometry. Real
 * tiled photographic textures replace the output of this function later
 * without changing anything that calls it (see MaterialLibrary).
 */
export interface ProceduralPBRParams {
  size?: number;
  baseColor: THREE.Color;
  variation: number; // 0..1, how much albedo brightness varies
  bumpStrength: number; // how pronounced the derived normal map is
  baseRoughness: number;
  roughnessVariation: number;
  noiseScale: number; // higher = finer noise detail
  stripeStrength?: number; // optional mower-stripe brightness modulation
  stripeFrequency?: number; // optional mower-stripe bands per texture tile
  stripeAngleRad?: number;
}

export interface ProceduralPBRSet {
  albedo: THREE.CanvasTexture;
  normal: THREE.CanvasTexture;
  roughness: THREE.CanvasTexture;
  ao: THREE.CanvasTexture;
}

/** Deterministic hash-based value noise — no external noise library needed. */
function hash2D(x: number, y: number): number {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453123;
  return s - Math.floor(s);
}

function valueNoise(x: number, y: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;

  const a = hash2D(xi, yi);
  const b = hash2D(xi + 1, yi);
  const c = hash2D(xi, yi + 1);
  const d = hash2D(xi + 1, yi + 1);

  const ux = xf * xf * (3 - 2 * xf);
  const uy = yf * yf * (3 - 2 * yf);

  return a * (1 - ux) * (1 - uy) + b * ux * (1 - uy) + c * (1 - ux) * uy + d * ux * uy;
}

/** Layered value noise (a cheap fractal-noise stand-in for Perlin/Simplex). */
function fractalNoise(x: number, y: number, octaves = 4): number {
  let total = 0;
  let amplitude = 0.5;
  let frequency = 1;
  let maxValue = 0;
  for (let i = 0; i < octaves; i++) {
    total += valueNoise(x * frequency, y * frequency) * amplitude;
    maxValue += amplitude;
    amplitude *= 0.5;
    frequency *= 2;
  }
  return total / maxValue;
}

function heightAt(px: number, py: number, size: number, noiseScale: number): number {
  return fractalNoise((px / size) * noiseScale, (py / size) * noiseScale);
}

export function generateProceduralPBRSet(params: ProceduralPBRParams): ProceduralPBRSet {
  const size = params.size ?? 256;

  const albedoCanvas = document.createElement('canvas');
  albedoCanvas.width = size;
  albedoCanvas.height = size;
  const albedoCtx = albedoCanvas.getContext('2d')!;
  const albedoImage = albedoCtx.createImageData(size, size);

  const normalCanvas = document.createElement('canvas');
  normalCanvas.width = size;
  normalCanvas.height = size;
  const normalCtx = normalCanvas.getContext('2d')!;
  const normalImage = normalCtx.createImageData(size, size);

  const roughnessCanvas = document.createElement('canvas');
  roughnessCanvas.width = size;
  roughnessCanvas.height = size;
  const roughnessCtx = roughnessCanvas.getContext('2d')!;
  const roughnessImage = roughnessCtx.createImageData(size, size);

  const aoCanvas = document.createElement('canvas');
  aoCanvas.width = size;
  aoCanvas.height = size;
  const aoCtx = aoCanvas.getContext('2d')!;
  const aoImage = aoCtx.createImageData(size, size);

  const { r: baseR, g: baseG, b: baseB } = params.baseColor;
  const step = 1; // finite-difference step for normal derivation, in pixels

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const idx = (y * size + x) * 4;
      const n = heightAt(x, y, size, params.noiseScale); // 0..1

      // Albedo: base color modulated by noise brightness plus optional mower striping.
      const stripeAxis =
        Math.cos(params.stripeAngleRad ?? -0.35) * (x / size) +
        Math.sin(params.stripeAngleRad ?? -0.35) * (y / size);
      const stripe = Math.sin(stripeAxis * Math.PI * 2 * (params.stripeFrequency ?? 0));
      const stripeBoost = 1 + stripe * (params.stripeStrength ?? 0);
      const brightness = (1 - params.variation / 2 + n * params.variation) * stripeBoost;
      albedoImage.data[idx + 0] = clamp255(baseR * 255 * brightness);
      albedoImage.data[idx + 1] = clamp255(baseG * 255 * brightness);
      albedoImage.data[idx + 2] = clamp255(baseB * 255 * brightness);
      albedoImage.data[idx + 3] = 255;

      // Normal: finite-difference of the same noise field used for albedo variation
      const hL = heightAt(x - step, y, size, params.noiseScale);
      const hR = heightAt(x + step, y, size, params.noiseScale);
      const hD = heightAt(x, y - step, size, params.noiseScale);
      const hU = heightAt(x, y + step, size, params.noiseScale);
      const nx = (hL - hR) * params.bumpStrength;
      const nz = (hD - hU) * params.bumpStrength;
      const normal = new THREE.Vector3(nx, nz, 1).normalize();
      normalImage.data[idx + 0] = clamp255((normal.x * 0.5 + 0.5) * 255);
      normalImage.data[idx + 1] = clamp255((normal.y * 0.5 + 0.5) * 255);
      normalImage.data[idx + 2] = clamp255((normal.z * 0.5 + 0.5) * 255);
      normalImage.data[idx + 3] = 255;

      // Roughness: base +/- noise-driven variation
      const roughness = params.baseRoughness + (n - 0.5) * params.roughnessVariation;
      const roughnessByte = clamp255(roughness * 255);
      roughnessImage.data[idx + 0] = roughnessByte;
      roughnessImage.data[idx + 1] = roughnessByte;
      roughnessImage.data[idx + 2] = roughnessByte;
      roughnessImage.data[idx + 3] = 255;

      // AO: mostly bright, darkened slightly in noise "crevices" (low height)
      const ao = 0.75 + n * 0.25;
      const aoByte = clamp255(ao * 255);
      aoImage.data[idx + 0] = aoByte;
      aoImage.data[idx + 1] = aoByte;
      aoImage.data[idx + 2] = aoByte;
      aoImage.data[idx + 3] = 255;
    }
  }

  albedoCtx.putImageData(albedoImage, 0, 0);
  normalCtx.putImageData(normalImage, 0, 0);
  roughnessCtx.putImageData(roughnessImage, 0, 0);
  aoCtx.putImageData(aoImage, 0, 0);

  const albedo = finalizeTexture(albedoCanvas, THREE.SRGBColorSpace);
  const normal = finalizeTexture(normalCanvas, THREE.NoColorSpace);
  const roughness = finalizeTexture(roughnessCanvas, THREE.NoColorSpace);
  const ao = finalizeTexture(aoCanvas, THREE.NoColorSpace);

  return { albedo, normal, roughness, ao };
}

function finalizeTexture(
  canvas: HTMLCanvasElement,
  colorSpace: THREE.ColorSpace,
): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = colorSpace;
  texture.anisotropy = 8;
  texture.needsUpdate = true;
  return texture;
}

function clamp255(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}
